/**
 * BD DASHBOARD - Business Development Revenue Tracker
 * ====================================================
 * Filtered lens on Booking Master for BD team
 * Focus: Post-registration bookings (REGISTRATION_DONE → REVENUE_RECEIVED)
 * 
 * Tabs:
 * 1. Registration Pending Invoice
 * 2. Invoice Raised (Pending Revenue)
 * 3. Revenue Received
 * 4. All Post-Registration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  FileText, IndianRupee, Clock, TrendingUp, 
  CheckCircle2, AlertTriangle, RefreshCw, 
  Building2, Calendar, Filter, ArrowRight, Menu
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import UnifiedBookingTable from '../components/UnifiedBookingTable';
import { MobileMenuTrigger } from '../components/Layout';

// === TAB CONFIGURATION ===
const TABS = [
  { 
    id: 'registration_pending', 
    label: 'Pending Invoice', 
    shortLabel: 'Pending',
    status: ['REGISTRATION_DONE'],
    icon: FileText,
    color: 'indigo'
  },
  { 
    id: 'invoice_raised', 
    label: 'Invoice Raised', 
    shortLabel: 'Invoiced',
    status: ['INVOICE_RAISED'],
    icon: IndianRupee,
    color: 'amber'
  },
  { 
    id: 'revenue_received', 
    label: 'Revenue Received', 
    shortLabel: 'Received',
    status: ['REVENUE_RECEIVED'],
    icon: CheckCircle2,
    color: 'emerald'
  },
  { 
    id: 'all_post_reg', 
    label: 'All Post-Registration', 
    shortLabel: 'All',
    status: ['REGISTRATION_DONE', 'INVOICE_RAISED', 'REVENUE_RECEIVED'],
    icon: Building2,
    color: 'slate'
  }
];

// === METRIC CARD COMPONENT ===
const MetricCard = ({ label, value, icon: Icon, color = 'slate', subtext }) => {
  const colorClasses = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="p-2 rounded-lg bg-white/80">
          <Icon size={18} className="text-current" />
        </div>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-current/70 mt-1">{label}</p>
      {subtext && <p className="text-xs text-current/50 mt-1">{subtext}</p>}
    </div>
  );
};

// === MAIN COMPONENT ===
export default function BDRevenueDashboard({ user }) {
  const [activeTab, setActiveTab] = useState('registration_pending');
  const [period, setPeriod] = useState('mtd');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    registration_pending: { count: 0, av: 0, avgDays: 0 },
    invoice_raised: { count: 0, av: 0, avgDays: 0 },
    revenue_received: { count: 0, av: 0 },
    all_post_reg: { count: 0, av: 0 }
  });
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch summary data
  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch all post-registration bookings
      const response = await api.get('/api/booking-master/unified-booking-list', {
        params: {
          status: 'REGISTRATION_DONE,INVOICE_RAISED,REVENUE_RECEIVED',
          limit: 1000,
          period
        }
      });
      
      const bookings = response.data.bookings || [];
      
      // Calculate summaries
      const regPending = bookings.filter(b => b.status === 'REGISTRATION_DONE');
      const invoiceRaised = bookings.filter(b => b.status === 'INVOICE_RAISED');
      const revenueReceived = bookings.filter(b => b.status === 'REVENUE_RECEIVED');
      
      setSummary({
        registration_pending: {
          count: regPending.length,
          av: regPending.reduce((sum, b) => sum + (b.av_lacs || 0), 0),
          avgDays: regPending.length > 0 
            ? Math.round(regPending.reduce((sum, b) => sum + (b.days_in_current_stage || 0), 0) / regPending.length)
            : 0
        },
        invoice_raised: {
          count: invoiceRaised.length,
          av: invoiceRaised.reduce((sum, b) => sum + (b.av_lacs || 0), 0),
          avgDays: invoiceRaised.length > 0
            ? Math.round(invoiceRaised.reduce((sum, b) => sum + (b.days_in_current_stage || 0), 0) / invoiceRaised.length)
            : 0
        },
        revenue_received: {
          count: revenueReceived.length,
          av: revenueReceived.reduce((sum, b) => sum + (b.av_lacs || 0), 0)
        },
        all_post_reg: {
          count: bookings.length,
          av: bookings.reduce((sum, b) => sum + (b.av_lacs || 0), 0)
        }
      });
    } catch (error) {
      console.error('Error fetching BD summary:', error);
      toast.error('Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    toast.success('Data refreshed');
  };

  const activeTabConfig = TABS.find(t => t.id === activeTab);
  const currentSummary = summary[activeTab];

  // Calculate realized revenue percentage
  const realizedPct = summary.all_post_reg.av > 0 
    ? Math.round((summary.revenue_received.av / summary.all_post_reg.av) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6" data-testid="bd-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          {/* Mobile Hamburger Menu */}
          <MobileMenuTrigger user={user} className="-ml-2" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">BD Revenue Tracker</h1>
            <p className="text-sm text-slate-500">Post-registration booking pipeline</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[120px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mtd">MTD</SelectItem>
              <SelectItem value="qtd">QTD</SelectItem>
              <SelectItem value="ytd">YTD</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Summary Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard 
          label="Pending Invoice"
          value={summary.registration_pending.count}
          icon={FileText}
          color="indigo"
          subtext={`₹${summary.registration_pending.av.toFixed(1)}L AV`}
        />
        <MetricCard 
          label="Invoice Raised"
          value={summary.invoice_raised.count}
          icon={IndianRupee}
          color="amber"
          subtext={`₹${summary.invoice_raised.av.toFixed(1)}L AV`}
        />
        <MetricCard 
          label="Revenue Received"
          value={summary.revenue_received.count}
          icon={CheckCircle2}
          color="emerald"
          subtext={`₹${summary.revenue_received.av.toFixed(1)}L AV`}
        />
        <MetricCard 
          label="Realized Revenue"
          value={`${realizedPct}%`}
          icon={TrendingUp}
          color={realizedPct >= 50 ? 'emerald' : realizedPct >= 25 ? 'amber' : 'red'}
          subtext={`of ₹${summary.all_post_reg.av.toFixed(1)}L total`}
        />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map(tab => {
          const tabSummary = summary[tab.id];
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          
          return (
            <Button
              key={tab.id}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 ${isActive ? 'bg-slate-900' : ''}`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.shortLabel}</span>
              <Badge variant="secondary" className="ml-1 bg-white/20">
                {tabSummary?.count || 0}
              </Badge>
            </Button>
          );
        })}
      </div>

      {/* Tab-specific Info Bar */}
      {activeTab !== 'all_post_reg' && (
        <Card className="p-3 mb-4 bg-slate-50 border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Building2 size={14} className="text-slate-500" />
                <span className="text-sm text-slate-600">
                  <strong>{currentSummary?.count || 0}</strong> bookings
                </span>
              </div>
              <div className="flex items-center gap-2">
                <IndianRupee size={14} className="text-slate-500" />
                <span className="text-sm text-slate-600">
                  <strong>₹{(currentSummary?.av || 0).toFixed(1)}L</strong> total AV
                </span>
              </div>
              {currentSummary?.avgDays !== undefined && activeTab !== 'revenue_received' && (
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-slate-500" />
                  <span className="text-sm text-slate-600">
                    <strong>{currentSummary?.avgDays || 0}</strong> avg days in stage
                  </span>
                </div>
              )}
            </div>
            
            {activeTab === 'registration_pending' && currentSummary?.avgDays > 10 && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertTriangle size={12} />
                Attention needed
              </Badge>
            )}
          </div>
        </Card>
      )}

      {/* Unified Booking Table */}
      <Card className="p-4">
        <UnifiedBookingTable
          key={`${activeTab}-${period}-${refreshKey}`}
          role="bd"
          defaultFilters={{
            status: activeTabConfig?.status?.join(','),
            period
          }}
          columnsVisible={[
            'booking_id', 'customer_name', 'project_name', 
            'rep_name', 'av_lacs', 'status', 'days_in_current_stage'
          ]}
          onStatusUpdate={handleRefresh}
        />
      </Card>

      {/* BD Permissions Note */}
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>BD Permissions:</strong> You can update bookings to{' '}
            <Badge variant="outline" className="mx-1">Invoice Raised</Badge> or{' '}
            <Badge variant="outline" className="mx-1">Revenue Received</Badge>.
            Verification stages and registrations are managed by Sales/Strategy teams.
          </div>
        </div>
      </div>
    </div>
  );
}
